import React, { useState, useEffect } from 'react';
import ModalOverlay from '../../shared/ModalOverlay';
import PocModal from '../pocs/PocModal';
import { Vendor, POC } from '../../types';

interface VendorModalProps {
  active: boolean;
  onClose: () => void;
  onSave: (data: Partial<Vendor>) => void;
  vendor?: Vendor | null;
  pocs: POC[];
  onSavePoc: (data: Partial<POC>) => Promise<void> | void;
  onUpdatePoc: (id: number, data: Partial<POC>) => Promise<void> | void;
  onDeletePoc: (id: number) => void;
}

const emptyVendor: Partial<Vendor> = {
  vendor_name: '', vendor_type: '',
  loc_address: '', loc_city: '', loc_state: '', loc_country: '', loc_zip: '', loc_phone: '',
  ship_address: '', ship_city: '', ship_state: '', ship_country: '', ship_zip: '', ship_phone: '',
};

/**
 * The phone number to show for a POC: the office number when present, otherwise the
 * cell number suffixed with ' (c)'. Office wins when both are set.
 */
const pocPhoneDisplay = (poc: POC): string => {
  const office = (poc.office_phone || '').trim();
  if (office) return office;
  const cell = (poc.cell_number || '').trim();
  return cell ? `${cell} (c)` : '';
};

const VendorModal: React.FC<VendorModalProps> = ({ active, onClose, onSave, vendor, pocs, onSavePoc, onUpdatePoc, onDeletePoc }) => {
  const [form, setForm] = useState<Partial<Vendor>>(emptyVendor);
  const [sameAsLocation, setSameAsLocation] = useState(false);
  const [pocModalOpen, setPocModalOpen] = useState(false);
  const [editVendorPoc, setEditVendorPoc] = useState<POC | null>(null);

  useEffect(() => {
    if (vendor) {
      setForm(vendor);
    } else {
      setForm(emptyVendor);
    }
    setSameAsLocation(false);
    setPocModalOpen(false);
    setEditVendorPoc(null);
  }, [vendor, active]);

  // POCs are linked to a vendor by matching vendor_name, so the list is scoped to the saved
  // vendor. POCs can only be added once the vendor exists (a POC needs a vendor name to link to).
  const vendorPocs = vendor ? pocs.filter((p) => p.vendor === vendor.vendor_name) : [];

  const handleAddPocClick = (): void => { setEditVendorPoc(null); setPocModalOpen(true); };
  const handleEditPocClick = (poc: POC): void => { setEditVendorPoc(poc); setPocModalOpen(true); };

  const handleSaveVendorPoc = async (data: Partial<POC>): Promise<void> => {
    if (editVendorPoc) {
      await onUpdatePoc(editVendorPoc.id, data);
    } else {
      await onSavePoc(data);
    }
    setPocModalOpen(false);
    setEditVendorPoc(null);
  };

  const handleChange = (field: keyof Vendor, value: string): void => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (sameAsLocation && (field as string).startsWith('loc_')) {
        const shipField = (field as string).replace('loc_', 'ship_') as keyof Vendor;
        (updated as Record<string, string | number | undefined>)[shipField] = value;
      }
      return updated;
    });
  };

  const handleSameAsLocation = (checked: boolean) => {
    setSameAsLocation(checked);
    if (checked) {
      setForm((prev) => ({
        ...prev,
        ship_address: prev.loc_address,
        ship_city: prev.loc_city,
        ship_state: prev.loc_state,
        ship_country: prev.loc_country,
        ship_zip: prev.loc_zip,
        ship_phone: prev.loc_phone,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <ModalOverlay active={active} onClose={onClose}>
      <div className="modal">
        <div className="modal-header">
          <h2>{vendor ? 'Edit Vendor' : 'New Vendor'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="vendor-type-row">
            <div className="form-group">
              <label>Vendor Name</label>
              <input type="text" value={form.vendor_name || ''} onChange={(e) => handleChange('vendor_name', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Vendor Type</label>
              <select value={form.vendor_type || ''} onChange={(e) => handleChange('vendor_type', e.target.value)} required>
                <option value="">Select…</option>
                <option value="Component Maintenance">Component Maintenance</option>
                <option value="145-MRO">145-MRO</option>
              </select>
            </div>
          </div>
          <div className="vendor-columns">
            <div className="vendor-col">
              <h3 className="vendor-col-title">Location</h3>
              {(['address', 'city', 'state', 'country', 'zip', 'phone'] as const).map((f) => (
                <div key={f} className="form-group">
                  <label>{f === 'state' ? 'State / Province' : f === 'zip' ? 'Zip Code' : f === 'phone' ? 'Phone Number' : f.charAt(0).toUpperCase() + f.slice(1)}</label>
                  <input
                    type={f === 'phone' ? 'tel' : 'text'}
                    value={form[`loc_${f}` as keyof Vendor] || ''}
                    onChange={(e) => handleChange(`loc_${f}` as keyof Vendor, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <div className="vendor-col">
              <div className="vendor-col-header">
                <h3 className="vendor-col-title">Shipping</h3>
                <label className="same-as-location">
                  <span>Same as Location</span>
                  <input type="checkbox" checked={sameAsLocation} onChange={(e) => handleSameAsLocation(e.target.checked)} />
                </label>
              </div>
              {(['address', 'city', 'state', 'country', 'zip', 'phone'] as const).map((f) => (
                <div key={f} className="form-group">
                  <label>{f === 'state' ? 'State / Province' : f === 'zip' ? 'Zip Code' : f === 'phone' ? 'Phone Number' : f.charAt(0).toUpperCase() + f.slice(1)}</label>
                  <input
                    type={f === 'phone' ? 'tel' : 'text'}
                    value={form[`ship_${f}` as keyof Vendor] || ''}
                    onChange={(e) => handleChange(`ship_${f}` as keyof Vendor, e.target.value)}
                    readOnly={sameAsLocation}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="subtable-section">
            <div className="subtable-header">
              <h3>Points of Contact</h3>
              {vendor && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddPocClick} title="Add POC to this vendor">+</button>
              )}
            </div>
            {vendor ? (
              <table className="subtable">
                <thead>
                  <tr>
                    <th>POC Name</th>
                    <th>Position</th>
                    <th>Phone Number</th>
                    <th>Email</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {vendorPocs.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '0.5rem' }}>No points of contact</td>
                    </tr>
                  ) : (
                    vendorPocs.map((p) => (
                      <tr key={p.id}>
                        <td>{p.poc_name}</td>
                        <td>{p.poc_position}</td>
                        <td>{pocPhoneDisplay(p)}</td>
                        <td>{p.email}</td>
                        <td style={{ display: 'flex', gap: '0.3rem', justifyContent: 'flex-end' }}>
                          <button type="button" className="btn-icon edit" title="Edit POC" onClick={() => handleEditPocClick(p)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                            </svg>
                          </button>
                          <button type="button" className="btn-icon delete" title="Delete POC" onClick={() => onDeletePoc(p.id)}>&#128465;</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <p style={{ color: 'var(--text-dim)', padding: '0.5rem' }}>Save the vendor first to add points of contact.</p>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save Vendor</button>
          </div>
        </form>
      </div>
      <PocModal
        active={pocModalOpen}
        onClose={() => { setPocModalOpen(false); setEditVendorPoc(null); }}
        onSave={handleSaveVendorPoc}
        poc={editVendorPoc}
        vendors={vendor ? [vendor] : []}
        lockedVendor={vendor || null}
      />
    </ModalOverlay>
  );
};

export default VendorModal;
